import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { balanceApi } from 'helpers/api/balance';
import { rentSelector } from 'store/transferAct/selectors';
import { useSelector } from 'react-redux';

import { Icon } from 'components/Icon';
import { HiddenInput } from 'components/HiddenInput';
import { ButtonIcon } from 'components/Button';
import { CalculatorModal } from 'components/CalculatorModal';

const TableInstruments = ({
  data,
  opened,
  canChange,
  rentSum,
  handleRemoveComponent,
  handleAddComponent,
  handleUpdateComponent,
  handleRemove,
  handleChangeCalculator,
  handleOpen,
  onUpdate,
  rateVAT,
  calculator,
  handleCalculator,
  isFullPledge,
  setIsFullPledge,
}) => {
  const rent = useSelector(rentSelector);

  const recalculateRentSum = (inst) => {
    balanceApi.calculateInstrument(inst).then(({ data } = {}) => {
      handleChangeCalculator(data);
    });
  };

  const onRentTermChange = (inst, value) => {
    const newInst = { ...inst, rentTerm: value };
    onUpdate(inst.id, newInst);
    recalculateRentSum(newInst);
  };

  const onQuantityChange = (inst, value) => {
    const newInst = { ...inst, quantity: value };
    onUpdate(inst.id, newInst);
    recalculateRentSum(newInst);
  };

  const isAdditionalRentTermNotEmpty = useCallback(() => {
    return rent.AdditionalRentTerm > 0;
  }, [data]);

  return (
    <table className="table-instruments">
      <thead>
        <tr className="table-inst-htr">
          <th className="table-inst-th" width="30px">
            №
          </th>
          <th className="table-inst-th" width="16px" style={{ borderRight: 'none', textAlign: 'left' }}></th>
          <th className="table-inst-th" align="start">
            Наименование
          </th>
          <th className="table-inst-th" width="50px">
            Кол-во
          </th>
          <th className="table-inst-th" width="75px">
            Срок аренды
          </th>
          {isAdditionalRentTermNotEmpty() && (
            <th className="table-inst-th" width="75px">
              Доп. срок
            </th>
          )}
          {!!rateVAT && (
            <>
              <th className="table-inst-th" width="50px">
                Ставка НДС
              </th>
              <th className="table-inst-th" width="75px">
                Сумма НДС
              </th>
            </>
          )}
          <th className="table-inst-th" width="75px">
            Залог
          </th>
          <th className="table-inst-th" width="95px">
            Сумма аренды
          </th>
          <th className="table-inst-th" width="75px"></th>
        </tr>
      </thead>

      <tbody>
        {data &&
          data.map((inst, i) => {
            let icon = 'instrument';

            if (inst.is_priced_by_wear) {
              icon = 'wheel';
            } else if (inst.components && opened.includes(inst.id)) {
              icon = 'tableminus';
            } else if (inst.components && !opened.includes(inst.id)) {
              icon = 'tableplus';
            }

            return (
              <React.Fragment key={inst.id}>
                <tr className="table-inst-tr" onClick={() => handleOpen(inst)}>
                  <th className="table-inst-th text-light">{i + 1}</th>

                  <th className="table-inst-th text-light" style={{ borderRight: 'none', textAlign: 'left' }}>
                    <Icon name={icon} style={{ margin: 'auto' }} />
                  </th>

                  <th className="table-inst-th text-light" align="start">
                    {inst.title}
                  </th>

                  <th className="table-inst-th" width="50px">
                    <HiddenInput
                      canChange={canChange}
                      value={inst.quantity - (inst.quantityOUT || 0)}
                      onChange={(value) => onQuantityChange(inst, value)}
                      min={1}
                    />
                  </th>

                  <th className="table-inst-th" width="75px">
                    {inst.is_priced_by_wear ? (
                      <div>-</div>
                    ) : (
                      <HiddenInput
                        canChange={canChange}
                        postfix=" сут."
                        value={inst.rentTerm || 0}
                        onChange={(value) => onRentTermChange(inst, value)}
                        min={1}
                      />
                    )}
                  </th>
                  {isAdditionalRentTermNotEmpty() && (
                    <th className="table-inst-th" width="75px">
                      {rent.AdditionalRentTerm} сут.
                    </th>
                  )}
                  {!!rateVAT && (
                    <>
                      <th className="table-inst-th" width="50px">
                        {rateVAT}%
                      </th>

                      <th className="table-inst-th" width="75px">
                        {((inst.itemRentSum * inst.quantity) / 100) * rateVAT} грн.
                      </th>
                    </>
                  )}

                  <th className="table-inst-th" width="75px">
                    {inst.pledge * inst.quantity} грн.
                  </th>

                  <th className="table-inst-th" width="95px">
                    {inst.itemRentSum * inst.quantity} грн.
                  </th>

                  <th className="table-inst-th double-th">
                    {canChange && <Icon name="trash" onClick={() => handleRemove(inst.id)} />}

                    <Icon name="calculator" onClick={() => handleCalculator(inst)} />

                    {calculator?.id === inst.id && (
                      <CalculatorModal
                        instrument={calculator || null}
                        onUpdate={handleChangeCalculator}
                        disabled={!canChange}
                        onClose={() => handleCalculator(null)}
                        isFullPledge={isFullPledge}
                        setIsFullPledge={setIsFullPledge}
                      />
                    )}
                  </th>
                </tr>

                {opened.includes(inst.id) &&
                  inst.components &&
                  inst.components.map((comp) => (
                    <tr key={comp.id} className="table-inst-tr">
                      <th className="table-inst-th text-light"></th>

                      <th className="table-inst-th text-light" style={{ borderRight: 'none', textAlign: 'left' }}></th>

                      <th className="table-inst-th text-light" align="start">
                        {comp.title}
                      </th>

                      <th className="table-inst-th">
                        <HiddenInput
                          canChange={canChange}
                          value={comp.quantity || 0}
                          onChange={(value) => handleUpdateComponent(inst, comp.id, value)}
                        />
                      </th>

                      <th className="table-inst-th" width="75px"></th>
                      {!!rateVAT && <th className="table-inst-th" width="50px"></th>}
                      {isAdditionalRentTermNotEmpty() && <th className="table-inst-th" width="75px"></th>}
                      <th className="table-inst-th" width="75px"></th>
                      <th className="table-inst-th" width="95px"></th>

                      <th className="table-inst-th double-th">
                        {canChange && (
                          <Icon
                            name="trash"
                            onClick={(e) => handleRemoveComponent(inst, comp.id)}
                            style={{ margin: 'auto' }}
                          />
                        )}
                      </th>
                    </tr>
                  ))}
                {opened.includes(inst.id) && canChange && (
                  <tr className="table-inst-tr" style={{ padding: '8px' }}>
                    <th className="table-inst-th text-light"></th>
                    <th className="table-inst-th text-light" style={{ borderRight: 'none', textAlign: 'left' }}></th>
                    <th className="table-inst-th text-light" align="start">
                      <ButtonIcon
                        className="btn-add-component"
                        onClick={() => handleAddComponent(inst)}
                        icon="plus"
                        text="Добавить компонент"
                      />
                    </th>

                    <th className="table-inst-th" width="50px"></th>
                    {isAdditionalRentTermNotEmpty() && <th className="table-inst-th" width="75px"></th>}
                    <th className="table-inst-th" width="75px"></th>
                    <th className="table-inst-th" width="75px"></th>

                    <th className="table-inst-th" width="50px"></th>
                    <th className="table-inst-th" width="75px"></th>
                    <th className="table-inst-th" width="75px"></th>
                    <th className="table-inst-th" width="95px"></th>

                    <th className="table-inst-th"> </th>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

        <tr className="table-inst-tr">
          <th className="table-inst-th text-light"></th>

          <th className="table-inst-th text-light" style={{ borderRight: 'none', textAlign: 'left' }}></th>

          <th className="table-inst-th text-light" align="start"></th>

          <th className="table-inst-th" width="50px"></th>

          <th className="table-inst-th" width="75px"></th>
          {isAdditionalRentTermNotEmpty() && <th className="table-inst-th" width="75px"></th>}
          {!!rateVAT && (
            <>
              <th className="table-inst-th" width="50px"></th>

              <th className="table-inst-th" width="75px">
                {rentSum.tax} грн.
              </th>
            </>
          )}

          <th className="table-inst-th" width="75px">
            {rentSum.pledge} грн.
          </th>

          <th className="table-inst-th" width="95px">
            {rentSum.itemSum} грн.
          </th>

          <th className="table-inst-th"> </th>
        </tr>
      </tbody>
    </table>
  );
};

TableInstruments.propTypes = {
  data: PropTypes.array,
  opened: PropTypes.array,
  canChange: PropTypes.bool,
  rentSum: PropTypes.object,
  handleRemoveComponent: PropTypes.func,
  handleAddComponent: PropTypes.func,
  handleUpdateComponent: PropTypes.func,
  handleRemove: PropTypes.func,
  handleChangeCalculator: PropTypes.func,
  handleOpen: PropTypes.func,
  onUpdate: PropTypes.func,
  rateVAT: PropTypes.number,
  calculator: PropTypes.object,
  handleCalculator: PropTypes.func,
  isFullPledge: PropTypes.bool,
  setIsFullPledge: PropTypes.func,
};

TableInstruments.defaultProps = {
  opened: [],
  canChange: true,
};

export { TableInstruments };
